import CaseClient from './CaseClient';
import { CaseActionHistory, CaseCommentLog, CloseCaseBulkActionOperation, RuleDashboardArgs } from '../../../domain/case/CaseDomains';
import HttpClient from '../../../gateway/HttpClient';
import { notBlank, notUndefined } from '../../../util/Assert';
import HttpMethod from '../../../constant/HttpMethod';
import { AlarmMethods } from '../../../constant/MethodNames';
import { GetCaseOptions } from '../../../service/CaseService';
import { query } from '@trlogic/client-js';
import { TransactionData } from '../../../domain/stream/transaction/TransactionDomains';
import { AxiosRequestConfig, CancelToken } from 'axios';
import { GraphConfiguration, GraphExecuteResponse, GraphInformationResponse, GraphNodeInformationConfiguration } from '../../../domain/graph/GraphDomains';
import ComponentName from '../../../constant/ComponentName';
import { User } from '../../../domain/rbac/RbacDomains';

class NestCaseClient implements CaseClient {
  private readonly httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    notUndefined(httpClient, 'Http client cannot be undefined');
    this.httpClient = httpClient;
  }

  public getNodeInformation(configuration: GraphNodeInformationConfiguration): Promise<GraphInformationResponse> {
    return this.httpClient.execute(HttpMethod.POST, AlarmMethods.GET_NODE_INFORMATION, configuration);
  }

  public getCaseCount(domain: string, _query: query.SelectQuery, token?: CancelToken): Promise<number> {
    notUndefined(domain, 'Domain cannot be undefined');
    notBlank(domain, 'Domain cannot be blank');
    notUndefined(_query, 'Query cannot be undefined');

    return this.httpClient.execute(HttpMethod.GET, AlarmMethods.GET_ALARM_COUNT, undefined, { domain, query: _query }, undefined, undefined, token);
  }

  public getCaseLogCount(domain: string, _query: query.SelectQuery): Promise<number> {
    notUndefined(domain, 'Domain cannot be undefined');
    notBlank(domain, 'Domain cannot be blank');
    notUndefined(_query, 'Query cannot be undefined');

    return this.httpClient.execute(HttpMethod.GET, AlarmMethods.GET_LOG_COUNT, undefined, { domain, query: _query });
  }

  public getActionHistory(ids: string[]): Promise<Record<string, CaseActionHistory>> {
    notUndefined(ids, 'Ids cannot be undefined');

    return this.httpClient.execute(HttpMethod.POST, AlarmMethods.GET_ACTION_HISTORY, { ids });
  }

  public goCase(domain: string, index: string, transaction: string): Promise<TransactionData> {
    notUndefined(domain, 'Domain cannot be undefined');
    notBlank(domain, 'Domain cannot be blank');
    notUndefined(index, 'Index cannot be undefined');
    notBlank(index, 'Index cannot be blank');
    notUndefined(transaction, 'Transaction cannot be undefined');
    notBlank(transaction, 'Transaction cannot be blank');

    const goCaseDto = {
      domain: domain,
      index: index,
      transaction: transaction,
    };
    return this.httpClient.execute(HttpMethod.POST, AlarmMethods.GO_ALARM, goCaseDto);
  }

  public nextCase(domain: string): Promise<{ _source: TransactionData } | undefined> {
    notUndefined(domain, 'Domain cannot be undefined');
    notBlank(domain, 'Domain cannot be blank');

    const nextCaseDto = {
      domain: domain,
    };
    return this.httpClient.execute(HttpMethod.POST, `${AlarmMethods.GET_NEXT_CASE}`, nextCaseDto);
  }

  public closeCase(domain: string, index: string, type: string, reason?: string): Promise<void> {
    notUndefined(domain, 'Domain cannot be undefined');
    notBlank(domain, 'Domain cannot be blank');
    notUndefined(index, 'Index cannot be undefined');
    notBlank(index, 'Index cannot be blank');
    notUndefined(type, 'Type cannot be undefined');
    notBlank(type, 'Type cannot be blank');

    const closeCaseDto = {
      domain: domain,
      index: index,
      type: type,
      reason: reason,
    };
    return this.httpClient.execute(HttpMethod.POST, AlarmMethods.CLOSE_CASE, closeCaseDto);
  }

  public holdCase(domain: string, index: string, reason?: string): Promise<void> {
    const holdCaseDto = {
      domain: domain,
      index: index,
      reason: reason,
    };
    return this.httpClient.execute(HttpMethod.POST, AlarmMethods.HOLD_CASE, holdCaseDto);
  }

  public closeBulk(domain: string, ids: CloseCaseBulkActionOperation[], type: string, reason?: string): Promise<void> {
    notUndefined(domain, 'Domain cannot be undefined');
    notBlank(domain, 'Domain cannot be blank');
    notUndefined(type, 'Type cannot be undefined');
    notBlank(type, 'Type cannot be blank');

    const closeBulkCaseDto = {
      domain: domain,
      ids: ids,
      type: type,
      reason: reason,
    };
    return this.httpClient.execute(HttpMethod.POST, AlarmMethods.CLOSE_BULK, closeBulkCaseDto);
  }

  public actionBulk(action: string, ids: TransactionData[]): Promise<void> {
    notUndefined(action, 'Action cannot be undefined');
    notBlank(action, 'Action cannot be blank');

    const actionBulkDto = {
      action,
      ids,
    };
    return this.httpClient.execute(HttpMethod.POST, AlarmMethods.ACTION_BULK, actionBulkDto);
  }

  public addComment(domain: string, index: string, content: string): Promise<CaseCommentLog> {
    notUndefined(domain, 'Domain cannot be undefined');
    notBlank(domain, 'Domain cannot be blank');
    notUndefined(index, 'Index cannot be undefined');
    notBlank(index, 'Index cannot be blank');
    notUndefined(content, 'Content cannot be undefined');
    notBlank(content, 'Content cannot be blank');

    const commentPayload = {
      domain: domain,
      index: index,
      comment: content,
    };
    return this.httpClient.execute(HttpMethod.POST, AlarmMethods.COMMENT, commentPayload);
  }

  public commentEditCase(index: string, comment: string, id: string): Promise<void> {
    notUndefined(index, 'Index cannot be undefined');
    notBlank(index, 'Index cannot be blank');
    notUndefined(comment, 'Comment cannot be undefined');
    notBlank(comment, 'Comment cannot be blank');
    notUndefined(id, 'Id cannot be undefined');
    notBlank(id, 'Id cannot be blank');

    const commentPayload = {
      index: index,
      comment: comment,
      id: id,
    };
    return this.httpClient.execute(HttpMethod.POST, AlarmMethods.COMMENT_EDIT, commentPayload);
  }

  public commentDomain(domain: string, comment: string): Promise<void> {
    notUndefined(domain, 'Domain cannot be undefined');
    notBlank(domain, 'Domain cannot be blank');
    notUndefined(comment, 'Comment cannot be undefined');
    notBlank(comment, 'Comment cannot be blank');

    const commentPayload = {
      domain: domain,
      comment: comment,
    };
    return this.httpClient.execute(HttpMethod.POST, AlarmMethods.COMMENT_DOMAIN, commentPayload);
  }

  public commentEditDomain(domain: string, comment: string, id: string): Promise<void> {
    notUndefined(domain, 'Domain cannot be undefined');
    notBlank(domain, 'Domain cannot be blank');
    notUndefined(comment, 'Comment cannot be undefined');
    notBlank(comment, 'Comment cannot be blank');
    notUndefined(id, 'Id cannot be undefined');
    notBlank(id, 'Id cannot be blank');

    const commentPayload = {
      domain: domain,
      comment: comment,
      id: id,
    };
    return this.httpClient.execute(HttpMethod.POST, AlarmMethods.COMMENT_EDIT_DOMAIN, commentPayload);
  }

  public addAttachment(domain: string, index: string, file: File): Promise<string> {
    notUndefined(domain, 'Domain cannot be undefined');
    notBlank(domain, 'Domain cannot be blank');
    notUndefined(index, 'Index cannot be undefined');
    notBlank(index, 'Index cannot be blank');
    notUndefined(file, 'File cannot be undefined');

    const attachmentPayload = new FormData();
    attachmentPayload.append('file', file);
    attachmentPayload.append('domain', domain);
    attachmentPayload.append('index', index);
    return this.httpClient.execute(HttpMethod.POST, AlarmMethods.ATTACHMENT, attachmentPayload);
  }

  public attachmentDomain(domain: string, file: File): Promise<void> {
    notUndefined(domain, 'Domain cannot be undefined');
    notBlank(domain, 'Domain cannot be blank');
    notUndefined(file, 'File cannot be undefined');

    const attachmentPayload = new FormData();
    attachmentPayload.append('file', file);
    attachmentPayload.append('domain', domain);
    return this.httpClient.execute(HttpMethod.POST, AlarmMethods.ATTACHMENT_DOMAIN, attachmentPayload);
  }

  public getAttachment(file: string): Promise<Blob | string | undefined> {
    notUndefined(file, 'File cannot be undefined');
    notBlank(file, 'File cannot be blank');

    const req: AxiosRequestConfig = {
      url: `${AlarmMethods.ATTACHMENT}/${file}`,
      method: 'GET',
      responseType: 'blob',
    };
    return this.httpClient.executeWithRequest<{ data: Blob | string | undefined }>(req).then((r) => r?.data);
  }

  public getAttachmentDomain(file: string): Promise<Blob | string | undefined> {
    notUndefined(file, 'File cannot be undefined');
    notBlank(file, 'File cannot be blank');

    const req: AxiosRequestConfig = {
      url: `${AlarmMethods.ATTACHMENT_DOMAIN}/${file}`,
      method: 'GET',
      responseType: 'blob',
    };
    return this.httpClient.executeWithRequest<{ data: Blob | string | undefined }>(req).then((r) => r?.data);
  }

  public getEnrichmentVariable(enrichments: string[], transactionData: TransactionData): Promise<any> {
    notUndefined(enrichments, 'Enrichments cannot be undefined');
    const body = {
      enrichments,
      transactionData,
    };
    return this.httpClient.execute(HttpMethod.POST, AlarmMethods.GET_ENRICHMENT_VARIABLE, body);
  }

  public getScores(domain: string, options?: GetCaseOptions): Promise<Record<string, number>> {
    notUndefined(domain, 'Domain cannot be undefined');
    notBlank(domain, 'Domain cannot be blank');

    return this.httpClient.execute(HttpMethod.GET, AlarmMethods.GET_SCORES, undefined, { domain, options });
  }

  public getDashboard(args?: RuleDashboardArgs): Promise<any> {
    return this.httpClient.execute(HttpMethod.POST, AlarmMethods.GET_DASHBOARD, args);
  }

  public executeGraph(configuration: GraphConfiguration): Promise<GraphExecuteResponse> {
    return this.httpClient.execute(HttpMethod.POST, AlarmMethods.EXECUTE_GRAPH, configuration);
  }

  public executeSAR(nodes: any[], selectedTransaction: string): Promise<TransactionData[]> {
    const dto = { nodes, selectedTransaction };
    return this.httpClient.execute(HttpMethod.POST, AlarmMethods.EXECUTE_SAR, dto);
  }

  public assign(domain: string, index: string, assign: string): Promise<void> {
    notUndefined(domain, 'Domain cannot be undefined');
    notBlank(domain, 'Domain cannot be blank');
    notUndefined(index, 'Index cannot be undefined');
    notBlank(index, 'Index cannot be blank');
    notUndefined(assign, 'Assign cannot be undefined');
    notBlank(assign, 'Assign cannot be blank');

    const assignDto = {
      domain: domain,
      index: index,
      assign: assign,
    };
    return this.httpClient.execute(HttpMethod.POST, AlarmMethods.ASSIGN, assignDto);
  }

  public getAssignableCaseUsers(domain: string): Promise<User[]> {
    notUndefined(domain, 'Domain cannot be undefined');
    notBlank(domain, 'Domain cannot be blank');
    return this.httpClient.execute(HttpMethod.GET, AlarmMethods.ASSIGNABLE_USERS, undefined, { domain });
  }

  public getErrorLogCount(_query: query.SelectQuery, documentId: string, component: ComponentName): Promise<number> {
    return this.httpClient.execute(HttpMethod.GET, AlarmMethods.ERROR_LOG_COUNT, undefined, { query: _query, documentId, component });
  }

  public getCaseCounts(): Promise<Record<string, number>> {
    return this.httpClient.execute(HttpMethod.GET, AlarmMethods.GET_CASE_COUNTS);
  }

  public getCasePageData(
    channel: string | undefined,
    query: query.SelectQuery,
    token?: CancelToken,
  ): Promise<{
    data: TransactionData[];
    count: number;
  }> {
    const dto = {
      channel,
      query,
    };
    return this.httpClient.execute(HttpMethod.POST, AlarmMethods.GET_CASE_PAGE_DATA, dto, undefined, undefined, undefined, token);
  }
}

export default NestCaseClient;
